/**
 * Hydra Source Node Definitions
 *
 * Config-driven definitions for all Hydra source types.
 * Each source is defined purely by config - no separate classes needed.
 */

import { internals, params } from '@nodes/TypedEndpoints';
import { HydraSourceNode } from './HydraSourceNode';

import type { NodeDef } from '@runtime/IRuntime';
import { HydraConfig } from './modulates';
import { Dependencies } from '@/nodes/Dependencies';


// Source configurations: [type, shortName, hydraFn, endpoints with param order implied by object key order]
const sourceConfigs: HydraConfig[] = [
  {
    type: 'Osc',
    shortName: 'osc',
    hydraFn: 'osc',
    params: {
      frequency: { value: 60, min: 0, max: 200, step: 1 },
      sync: { value: 0.1, min: 0, max: 1, step: 0.001 },
      offset: { value: 0, min: -2, max: 2, step: 0.01 },
    }
  },
  {
    type: 'Noise',
    shortName: 'noise',
    hydraFn: 'noise',
    params: {
      scale: { value: 10, min: 0, max: 100, step: 0.5 },
      offset: { value: 0.1, min: 0, max: 2, step: 0.01 },
    }
  },
  {
    type: 'Shape',
    shortName: 'shape',
    hydraFn: 'shape',
    params: {
      sides: { value: 3, min: 2, max: 100, step: 1 },
      radius: { value: 0.3, min: 0, max: 1, step: 0.01 },
      smoothing: { value: 0.01, min: 0, max: 1, step: 0.001 },
    }
  },
  {
    type: 'Gradient',
    shortName: 'grad',
    hydraFn: 'gradient',
    params: {
      speed: { value: 0, min: -2, max: 2, step: 0.01 },
    }
  },
  {
    type: 'Voronoi',
    shortName: 'vor',
    hydraFn: 'voronoi',
    params: {
      scale: { value: 5, min: 0, max: 50, step: 0.5 },
      speed: { value: 0.3, min: 0, max: 2, step: 0.01 },
      blending: { value: 0.3, min: 0, max: 1, step: 0.01 },
    }
  },
  {
    type: 'Solid',
    shortName: 'solid',
    hydraFn: 'solid',
    params: {
      r: { value: 0, min: 0, max: 1, step: 0.01 },
      g: { value: 0, min: 0, max: 1, step: 0.01 },
      b: { value: 0, min: 0, max: 1, step: 0.01 },
      a: { value: 1, min: 0, max: 1, step: 0.01 },
    }
  },
  {
    type: 'Webcam',
    icon: 'videocam',
    hydraFn: '',
    shortName: 'cam',
    info: 'Webcam (attach Fa webcam and enable it)',
    params: {
    }
  }
];

// Generate node definitions from configs
export const sourceNodes: NodeDef[] = sourceConfigs.map(config => {

  const endpoints = {
    ...params(config.params),
    ...internals({
      hydraOut: { dataType: 'hydra', isOutput: true }
    })

  }
  return {
    type: config.type,
    shortName: config.shortName,
    category: 'source',
    endpoints,
    ctor: class extends HydraSourceNode<typeof endpoints> {
      constructor(id: string, deps: Dependencies) {
        super(id, deps, {
          hydraFn: config.hydraFn,
          fnArgs: Object.keys(config.params),
          type: config.type
        });
      }
    }
  }
});
